import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { Link } from "@tanstack/react-router";
import { useViewSearch } from "@/lib/view-search";
import { BudgetsPage } from "@/features/budgets/BudgetsPage";
import { CurrencyRatesPage } from "@/features/currencies/CurrencyRatesPage";
import { AutomationTab } from "./AutomationTab";
import { BooksSettingsPage } from "./BooksSettingsPage";
import { DocumentTemplatesTab } from "./DocumentTemplatesTab";
import { PaymentTermsTab } from "./PaymentTermsTab";
import { TaxSettingsPage } from "./TaxSettingsPage";

const LINK_CLASS = "font-medium text-[var(--action)] underline";

const SECTIONS = [
  { value: "documents", label: "Invoicing" },
  { value: "books", label: "Books" },
  { value: "currencies", label: "Currencies" },
  { value: "tax", label: "Tax" },
  { value: "planning", label: "Budgets" },
  { value: "automation", label: "Automation" },
];
const SECTION_VALUES = SECTIONS.map((item) => item.value);
// `/settings?tab=accounting` predates the direct Currencies tab.
const TAB_VALUES = [...SECTION_VALUES, "accounting"] as const;

export function SettingsPage() {
  const [section, setSection] = useViewSearch("tab", TAB_VALUES, "documents");
  const activeSection = section === "accounting" ? "currencies" : section;

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Settings"
        description="Accounting configuration for this organization. Team, access, and organization administration live in SYLVIQ Console."
      />

      <Tabs
        tabs={SECTIONS}
        value={activeSection}
        onValueChange={setSection}
        label="Settings sections"
      >
        {activeSection === "documents" ? (
          <div className="grid gap-5">
            <div className="grid gap-3">
              <h2 className="text-lg font-semibold tracking-tight">
                Document templates
              </h2>
              <DocumentTemplatesTab />
            </div>
            <div className="grid gap-3">
              <h2 className="text-lg font-semibold tracking-tight">
                Payment terms
              </h2>
              <PaymentTermsTab />
            </div>
          </div>
        ) : null}
        {activeSection === "books" ? <BooksSettingsPage embedded /> : null}
        {activeSection === "currencies" ? (
          <div className="grid gap-3">
            <h2 className="text-lg font-semibold tracking-tight">
              Currencies & rates
            </h2>
            <CurrencyRatesPage embedded />
          </div>
        ) : null}
        {activeSection === "tax" ? <TaxSettingsPage embedded /> : null}
        {activeSection === "planning" ? <BudgetsPage embedded /> : null}
        {activeSection === "automation" ? (
          <div className="grid gap-3">
            <h2 className="text-lg font-semibold tracking-tight">
              Nightly jobs
            </h2>
            <AutomationTab />
          </div>
        ) : null}
      </Tabs>

      <section
        aria-label="Related configuration"
        className="surface grid gap-2 rounded-2xl p-5 text-sm"
      >
        <p className="text-[var(--muted-foreground)]">
          Bank rules and reminder rules are configured on their working pages:
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          <Link to="/banking/rules" className={LINK_CLASS}>
            Bank rules
          </Link>
          <Link to="/reminders" search={{ section: "rules" }} className={LINK_CLASS}>
            Reminder rules
          </Link>
        </div>
      </section>
    </div>
  );
}
